import PropTypes from 'prop-types'
import { compose } from 'redux'
import { Helmet } from 'react-helmet'
import {
  BrandShape,
  ProductFiltersShape,
  SortParamsShape,
} from 'store/lib/prop-types'
import Image from 'components/image'
import { track } from 'lib/analytics'
import { babylistUrl, storeBrandPagePath } from 'lib/urls'
import { useBrand, useBrandProducts } from '../../api/hooks'
import { withActiveSortParams, withActiveProductFilters } from '../../reducers'
import ProductGrid, { mapHeroesToProductCards } from '../product-grid'
import FrequentlyAskedQuestions from '../FrequentlyAskedQuestions/FrequentlyAskedQuestions'
import ShopByGuides from '../shop-by-guides'

const HeroProductsBase = ({
  brandId,
  sortParams,
  productFilters,
  brandName,
  guides,
}) => {
  const { data, isLoading } = useBrandProducts(brandId, {
    filters: productFilters,
    orderBy: {
      key: sortParams?.sort,
      direction: sortParams?.order,
    },
  })

  const productCards = isLoading
    ? null
    : data.heroProducts.map(mapHeroesToProductCards)

  if (!isLoading && data.heroProducts.length > 0) {
    track.storeBrandViewed({
      eventLocation: track.EventLocation.STORE_MINUS_BRAND,
      brand: brandName,
    })
  }

  return (
    <ProductGrid
      activeSlugSet={null}
      className="mtl"
      guides={guides}
      productCards={productCards}
    />
  )
}

HeroProductsBase.propTypes = {
  brandId: PropTypes.number.isRequired,
  sortParams: PropTypes.shape(SortParamsShape),
  productFilters: PropTypes.shape(ProductFiltersShape),
  brandName: PropTypes.string.isRequired,
  guides: PropTypes.array,
}

HeroProductsBase.defaultProps = {
  sortParams: {},
  productFilters: {},
}

const HeroProducts = compose(
  withActiveProductFilters(),
  withActiveSortParams()
)(HeroProductsBase)

export const BrandPage = ({ params }) => {
  const { brandId, slug } = params
  const { data, isLoading } = useBrand(brandId)

  if (!data && isLoading) {
    return null
  }

  const {
    title,
    description,
    logoUrl,
    meta,
    name,
    frequentlyAskedQuestions,
    guides,
  } = data || {}

  return (
    <>
      <Helmet
        meta={[
          {
            name: 'description',
            content: meta.seoDescription,
          },
        ]}
        title={meta.seoTitle}
      >
        <link
          href={babylistUrl(storeBrandPagePath(slug, brandId))}
          rel="canonical"
        />
      </Helmet>
      <div className="flex col align-center justify-center well paxl">
        <Image
          alt={title}
          className="circle-mask"
          lazyLoad={false}
          src={logoUrl}
          style={{ width: 180, height: 180, background: '#e6e6e6' }}
        />
        <h1>{title}</h1>
        {description && <h2 className="text-center h5">{description}</h2>}
      </div>
      <HeroProducts brandId={brandId} brandName={name} guides={guides} />
      <ShopByGuides guides={guides} />
      {frequentlyAskedQuestions && frequentlyAskedQuestions.length > 0 && (
        <div className="mtxl">
          <FrequentlyAskedQuestions
            headerVariant="secondary"
            questions={frequentlyAskedQuestions}
          />
        </div>
      )}
    </>
  )
}

BrandPage.propTypes = {
  params: PropTypes.shape({
    slug: PropTypes.string,
    brandId: PropTypes.string,
  }).isRequired, // react-router params
}
